import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { ChromePicker } from "react-color";
import ItemSelectChip from "./ItemSelectChip";
import ModeEditIcon from "@mui/icons-material/ModeEdit";

const STATUS = {
  WAIT: 1,
  CREATING: 2,
  EXCLUDED: 3,
  ERROR: 0,
};

const getStatusChip = (status) => {
  const statusConfig = {
    1: { label: "대기", color: "#6B7280", bgColor: "#F3F4F6" },
    2: { label: "생성", color: "#2196F3", bgColor: "#E3F2FD" },
    3: { label: "제외", color: "#ED6C02", bgColor: "#FFF3E0" },
  };
  const config = statusConfig[status];
  return (
    <span
      style={{
        padding: "3px 14px",
        borderRadius: "8px",
        fontSize: "0.85rem",
        fontWeight: 500,
        backgroundColor: config.bgColor,
        color: config.color,
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
      }}
    >
      {config.label}
    </span>
  );
};

export function useColumns(itemData, updateRowExcludedStatus) {
  return [
    {
      accessorKey: "color",
      header: "색상",
      enableSorting: false,
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      size: 80,
      Cell: ({ row }) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              backgroundColor: row.original.color ?? "#000",
              border: "none",
              opacity: 1,
            }}
          />
        </div>
      ),
      Edit: ({ row, table }) => {
        const [color, setColor] = useState(row.original.color);
        const [value, setValue] = useState(row.original.color);
        const handleChangeComplete = (color) => {
          setColor(color.hex);
          table.setEditingRow((old) => ({
            ...old,
            color: color.hex,
          }));
        };
        const handleChange = (e) => {
          setValue(e.hex);
          table.setEditingRow((old) => ({
            ...old,
            color: e.hex,
          }));
        };
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <ChromePicker
              color={color}
              value={value}
              name="color"
              onChangeComplete={handleChangeComplete}
              onChange={handleChange}
            />
          </div>
        );
      },
    },
    {
      accessorKey: "order_name",
      header: "생산 지시 ID",
      size: 150,
      enableEditing: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Edit: ({ row, table }) => {
        const [value, setValue] = useState(row.original.order_name);
        const handleChange = (e) => {
          setValue(e.target.value);
          table.setEditingRow((old) => ({
            ...old,
            order_name: e.target.value,
          }));
        };
        return (
          <TextField
            size="normal"
            fullWidth
            required
            type="text"
            placeholder="생산 지시 ID"
            label="생산 지시 ID"
            name="order_name"
            value={value}
            onChange={handleChange}
          />
        );
      },
    },
    {
      accessorKey: "item_id",
      header: "제품명",
      size: 110,
      filterVariant: "select",
      enableColumnFilter: true,
      filterSelectOptions: itemData?.map((i) => i.item_name),
      Cell: ({ row }) => {
        console.log("itemData : ", itemData);
        console.log("row.original : ", row.original);
        const matchedItem = itemData?.filter(
          (content) => content.item_id === row.original.item_id
        )[0];
        return <div>{matchedItem?.item_name}</div>;
      },
      filterFn: (row, columnId, filterValue) => {
        const matchedItem = itemData?.find(
          (item) => item.item_id === row.original.item_id
        );
        return matchedItem?.item_name === filterValue;
      },
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Edit: ({ row, table }) => {
        const [selectedItem, setSelectedItem] = useState([]);
        useEffect(() => {
          const matchedItem = itemData.find(
            (item) => item.item_id === row.original.item_id
          );
          setSelectedItem(matchedItem ? [matchedItem.item_name] : []);
        }, [row.original.item_id]);
        const handleItemChange = (newSelectedItems) => {
          setSelectedItem(newSelectedItems);
          const selectedItemData = itemData.find(
            (item) => item.item_name === newSelectedItems[0]
          );
          if (selectedItemData) {
            const updatedValues = {
              ...row.original,
              item_id: selectedItemData.item_id,
            };
            table.setEditingRow((old) => ({
              ...old,
              ...updatedValues,
            }));
          }
        };
        return (
          <ItemSelectChip
            selectedItem={selectedItem}
            setSelectedItem={handleItemChange}
            itemList={itemData}
            disabledProps={false}
          />
        );
      },
    },
    {
      accessorKey: "qty",
      header: "수량",
      size: 80,
      filterVariant: "range",
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Edit: ({ row, table }) => {
        const [value, setValue] = useState(row.original.qty);
        const handleChange = (e) => {
          setValue(e.target.value);
          table.setEditingRow((old) => ({
            ...old,
            qty: e.target.value,
          }));
        };
        return (
          <TextField
            size="normal"
            fullWidth
            required
            type="number"
            placeholder="수량"
            label="수량"
            value={value}
            onChange={handleChange}
          />
        );
      },
    },
    {
      accessorKey: "order_priority",
      header: "우선순위",
      size: 50,
      filterVariant: "range",
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
    },
    {
      accessorKey: "start_date",
      header: "생산 시작",
      size: 230,
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Cell: ({ row }) => row.original.start_date.slice(0, 16),
      Edit: ({ row, table }) => {
        const [value, setValue] = useState(() => {
          return row.original.start_date
            ? dayjs(row.original.start_date)
            : null;
        });
        const handleDateChange = (newValue) => {
          setValue(newValue);
          const dateString = newValue
            ? newValue.format("YYYY-MM-DD HH:mm:ss")
            : null;
          table.setEditingRow((old) => ({
            ...old,
            start_date: dateString,
          }));
        };
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              value={value}
              onChange={handleDateChange}
              format="YYYY년 MM월 DD일 HH시 mm분"
              label="시작 시간"
            />
          </LocalizationProvider>
        );
      },
    },
    {
      accessorKey: "due_date",
      header: "납기일",
      size: 230,
      enableColumnFilter: false,
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      muiTableFooterCellProps: { align: "center" },
      Cell: ({ row }) => row.original.due_date?.slice(0, 16),
      Edit: ({ row, table }) => {
        const [value, setValue] = useState(() => {
          return row.original.due_date ? dayjs(row.original.due_date) : null;
        });
        const handleDateChange = (newValue) => {
          setValue(newValue);
          const dateString = newValue
            ? newValue.format("YYYY-MM-DD HH:mm:ss")
            : null;
          table.setEditingRow((old) => ({
            ...old,
            due_date: dateString,
          }));
        };
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              value={value}
              onChange={handleDateChange}
              format="YYYY년 MM월 DD일 HH시 mm분"
              label="납기일"
            />
          </LocalizationProvider>
        );
      },
    },
    // {
    //   accessorKey: "status",
    //   header: "직전 스케줄링",
    //   size: 100,
    //   Cell: ({ row }) => getStatusChip(row.original.status),
    //   filterVariant: "select",
    //   filterSelectOptions: [
    //     { text: "대기", value: STATUS.WAIT },
    //     { text: "생성", value: STATUS.CREATING },
    //     { text: "제외", value: STATUS.EXCLUDED },
    //   ],
    //   muiTableHeadCellProps: { align: "center" },
    //   muiTableBodyCellProps: { align: "center" },
    //   muiTableFooterCellProps: { align: "center" },
    // },
    {
      accessorKey: "is_excluded",
      header: "적용 여부",
      size: 80,
      enableColumnFilter: false,
      sortingFn: (rowA, rowB, columnId) => {
        const a = rowA.original.is_excluded;
        const b = rowB.original.is_excluded;
        if (a === b) return 0;
        return a ? 1 : -1;
      },
      muiTableHeadCellProps: { align: "center" },
      muiTableBodyCellProps: { align: "center" },
      Cell: ({ row }) => {
        const initialIsExcluded =
          row.original.is_excluded !== undefined
            ? row.original.is_excluded
            : row.original.status === 3 || row.original.status === 1;
        const [isExcluded, setIsExcluded] = useState(initialIsExcluded);
        useEffect(() => {
          const currentIsExcluded =
            row.original.is_excluded !== undefined
              ? row.original.is_excluded
              : row.original.status === 3 || row.original.status === 1;

          if (isExcluded !== currentIsExcluded) {
            setIsExcluded(currentIsExcluded);
          }
        }, [row.original.is_excluded, row.original.status]);
        const handleSwitchChange = (e) => {
          e.stopPropagation();
          const newValue = e.target.checked;
          setIsExcluded(newValue);
          row.original.is_excluded = newValue;
          updateRowExcludedStatus(row.original.order_id, newValue);
        };
        return (
          <>
            {isExcluded ? "제외" : "적용"}
            <FormControlLabel
              control={
                <Switch
                  checked={isExcluded}
                  onChange={handleSwitchChange}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    "& .MuiSwitch-switchBase": {
                      color: "#78c1ff",
                      "&:hover": {
                        backgroundColor: "rgba(199, 231, 252, 0.8)",
                      },
                      "&.Mui-checked": {
                        color: "#f44336",
                        "&:hover": {
                          backgroundColor: "rgba(250, 225, 227, 0.6)",
                        },
                      },
                    },
                    "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                      backgroundColor: "#c2e3ff",
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#ef9a9a",
                    },
                  }}
                />
              }
              label=""
              sx={{ margin: 0 }}
            />
          </>
        );
      },
    },
    // {
    //   accessorKey: "action2",
    //   size: 150,
    //   header: " ",
    //   enableSorting: false,
    //   Cell: ({ row, table }) => (
    //     <Button
    //       variant="outlined"
    //       startIcon={<ModeEditIcon />}
    //       onClick={(e) => {
    //         e.stopPropagation();
    //         table.setEditingRow(row);
    //       }}
    //       sx={{
    //         border: "1px solid #adb5bd",
    //         color: "black",
    //         "&: hover": {
    //           border: "1px solid #868e96",
    //         },
    //       }}
    //     >
    //       수정
    //     </Button>
    //   ),
    //   muiTableHeadCellProps: { align: "center" },
    //   muiTableBodyCellProps: { align: "center" },
    //   muiTableFooterCellProps: { align: "center" },
    //   enableColumnFilter: false,
    //   Edit: () => null,
    // },
  ];
}
